import {
	Banner,
	Button,
	Card,
	Checkbox,
	FormLayout,
	Heading,
	Modal,
	Stack,
	Tag,
	TextField,
} from "@shopify/polaris"
import React, { useContext, useEffect, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import convertCurrency from "../../utils/convertCurrency"
import { serializeIntoCSV } from "../../utils/csvExport.js"
import CollectionCreator from "../CollectionCreator"
import { CollectionPicker } from "../CollectionPicker.jsx"
import { ConfirmBeforeClosingModal } from "../ConfirmBeforeClosingModal.jsx"
import { CoolButton } from "../CoolButton.jsx"
import PriceRounding, { roundPrice } from "../PriceRounding"
import VendorField from "../VendorField.jsx"
import { AIBulkOrdersButtons } from "../ai/AIBulkOrdersButtons.jsx"
import { CreditsCostPreview } from "../ai/CreditsCostPreview.jsx"
import { AIStateContext } from "../providers/AIStateProvider.jsx"
import { GlobalStateContext } from "../providers/GlobalStateProvider"
import { ProductsSelector } from "./ProductsSelector.jsx"
import { ScopesUpdateTutorial } from "../ScopesUpdateTutorial.jsx"
import handleize from "../../utils/handleize.js"
import { sanitizeProductHtml } from "../../utils/sanitizeProductHtml.js"

export const MultipleProductsImportModal = (props) => {
	const { state, dispatch } = useContext(GlobalStateContext)
	const { t } = useTranslation()
	const { aiState, setAIState } = useContext(AIStateContext)

	const [products, setProducts] = useState([])
	const [selectedProductsIds, setSelectedProductsIds] = useState([])

	const [tags, setTags] = useState(state.defaultTags)
	const [tagString, setTagString] = useState("")

	const [statusActiveChecked, setStatusActiveChecked] = useState(
		state.statusActiveByDefault,
	)
	const [publishChecked, setPublishChecked] = useState(
		state.publishByDefault &&
			state.currentScopes?.includes("write_publications"),
	)

	const [showCollectionPicker, setShowCollectionPicker] = useState(false)
	const [showCollectionCreator, setShowCollectionCreator] = useState(false)
	const [collectionsSelected, setCollectionsSelected] = useState([])
	const [collectionsCreated, setCollectionsCreated] = useState(0)

	const [roundingEnabled, setRoundingEnabled] = useState(
		state.roundingEnabledByDefault,
	)
	const [roundingNumber, setRoundingNumber] = useState(
		state.roundingNumberByDefault,
	)

	const [vendor, setVendor] = useState(state.defaultVendor)
	const [taxable, setTaxable] = useState(state.taxableByDefault)

	const [csvLoading, setCsvLoading] = useState(false)

	const [
		confirmBeforeClosingModalIsOpen,
		setConfirmBeforeClosingModalIsOpen,
	] = useState(false)

	const [showUpdateScopesModal, setShowUpdateScopesModal] = useState(false)

	const resetState = () => {
		setProducts([])
		setSelectedProductsIds([])

		setTags(state.defaultTags)
		setTagString("")

		setStatusActiveChecked(state.statusActiveByDefault)
		setPublishChecked(
			state.publishByDefault &&
				state.currentScopes?.includes("write_publications"),
		)

		setShowCollectionPicker(false)
		setShowCollectionCreator(false)
		setCollectionsSelected([])
		setCollectionsCreated(0)

		setRoundingEnabled(state.roundingEnabledByDefault)
		setRoundingNumber(state.roundingNumberByDefault)

		setVendor(state.defaultVendor)
		setTaxable(state.taxableByDefault)

		setCsvLoading(false)
		setConfirmBeforeClosingModalIsOpen(false)
	}

	const addTag = () => {
		setTags([
			...tags,
			...tagString
				.split(",")
				.map((v) => v.trim())
				.filter((v) => v.length > 0),
		])
		setTagString("")
	}

	const removeTag = (tag) => {
		setTags(tags.filter((t) => tag !== t))
	}

	const submitProductsToUpload = () => {
		let productsToSubmit = products.filter((product) =>
			selectedProductsIds.includes(product.id),
		)

		let formattedProductsToSubmit = productsToSubmit.map((product) => {
			let productTags = []
			if (!product.tags) {
				productTags = [...tags]
			} else if (Array.isArray(product.tags)) {
				productTags = [...product.tags, ...tags]
			} else if (product.tags.length > 0 && product.tags.split) {
				productTags = [...product.tags.split(", "), ...tags]
			}

			return {
				id: product.id,
				title: product.title,
				descriptionHtml: sanitizeProductHtml(product.body_html),
				handle: product.handle || handleize(product.title),
				image: product.image?.src
					? {
							src: product.image.src,
							altText: product.image.alt || "",
							id: product.image.id || "",
						}
					: null,
				images:
					product.images
						?.map((img) => {
							return {
								altText: img.alt || "",
								src: img.src,
							}
						})
						.slice(0, 250) || [],
				variants:
					product.variants
						?.map((variant) => {
							let imageSrc = ""
							if (variant?.featured_image?.src) {
								imageSrc = variant.featured_image.src
							} else {
								product.images?.map((img) => {
									if (
										"id" in img &&
										"image_id" in variant &&
										img.id === variant.image_id &&
										imageSrc === ""
									) {
										imageSrc = img.src
									}
								})
							}

							let toReturn = {
								price:
									roundingEnabled && roundingNumber !== null
										? roundPrice(
												convertCurrency(
													variant.price,
													product._currency,
													state.shopCurrency,
												),
												roundingNumber,
											)
										: convertCurrency(
												variant.price,
												product._currency,
												state.shopCurrency,
											),
								options: [
									variant.option1 || "",
									variant.option2 || "",
									variant.option3 || "",
								],
								compareAtPrice:
									variant.compare_at_price !== "" &&
									parseFloat(variant.compare_at_price) >
										parseFloat(variant.price)
										? roundingEnabled &&
											roundingNumber !== null
											? roundPrice(
													convertCurrency(
														variant.compare_at_price,
														product._currency,
														state.shopCurrency,
													),
													roundingNumber,
												)
											: convertCurrency(
													variant.compare_at_price,
													product._currency,
													state.shopCurrency,
												)
										: null,
								taxable: taxable,
								sku: variant.sku || null,
							}
							if (imageSrc && imageSrc !== "") {
								toReturn.imageSrc = imageSrc
							}
							return toReturn
						})
						?.slice(0, 250) || [],
				options: product.options?.slice(0, 250) || [],
				tags: productTags
					.filter((tag) => tag !== "")
					.filter((tag) => tag !== "Kopy" && tag !== "kopy")
					.slice(0, 250),
				published: statusActiveChecked && publishChecked,
				status: statusActiveChecked ? "ACTIVE" : "DRAFT",
				vendor: vendor,
				collectionsToJoin:
					collectionsSelected.length > 0
						? collectionsSelected.map((selection) => selection.id)
						: [],
				_productUrl: product._productUrl || null,
			}
		})

		props.submit(formattedProductsToSubmit, aiState.bulkAIOrders || [])
	}

	const exportToCSV = () => {
		setCsvLoading(true)

		const { csvUrl, csvFilename } = serializeIntoCSV({
			collectionsSelected,
			products,
			productsUrl: props.productsUrl,
			publishChecked: statusActiveChecked && publishChecked,
			selectedProductsIds,
			tags,
			vendor,
			status: statusActiveChecked ? "active" : "draft",
		})

		setCsvLoading(false)
		props.onCsvExport(csvUrl, csvFilename)
	}

	const changePublishChecked = (change) => {
		if (
			change === true &&
			!state.currentScopes?.includes("write_publications")
		) {
			setShowUpdateScopesModal(true)
		} else {
			setPublishChecked(change)
		}
	}

	const changeStatusActiveChecked = (change) => {
		setStatusActiveChecked(change)
	}

	useEffect(() => {
		if (selectedProductsIds.length > 0) {
			const products = props.products.filter((p) =>
				selectedProductsIds.includes(p.id),
			)

			setAIState({
				bulkAIOrdersInputs: products.map((p) => ({
					title: p.title,
					body_html: p.body_html,
					variants: p.variants,
				})),
			})
		} else {
			setAIState({ bulkAIOrdersInputs: [] })
		}
	}, [selectedProductsIds])

	useEffect(() => {
		return () => {
			resetState()
		}
	}, [])

	useEffect(() => {
		if (props.modalIsOpen === true && props.products) {
			setProducts(props.products.map((p) => ({ ...p })))
			if (state.defaultVendor === "") {
				setVendor(props.products?.[0]?.vendor || "")
			}
		} else if (props.modalIsOpen === false) {
			resetState()
			setAIState({
				bulkAIOrders: [],
				bulkAIOrdersInputs: [],
				bulkAIOrdersTotalCost: 0,
				loadingBulkAIOrdersTotalCost: false,
			})
		}
	}, [props.modalIsOpen])

	const loadingCreditsAvailable =
		aiState.bulkAIOrders?.length && aiState.loadingCreditsAvailable
	const loadingAiOrdersTotalCost =
		aiState.bulkAIOrders?.length && aiState.loadingBulkAIOrdersTotalCost
	const insufficientCredits =
		aiState.bulkAIOrders?.length &&
		aiState.creditsAvailable < aiState.bulkAIOrdersTotalCost
	const importDisabled =
		loadingCreditsAvailable ||
		loadingAiOrdersTotalCost ||
		insufficientCredits

	return (
		<Modal
			large={true}
			open={props.modalIsOpen}
			onClose={() => setConfirmBeforeClosingModalIsOpen(true)}
			title={
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: "0.5rem",
					}}
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="1.4rem"
						height="1.4rem"
						viewBox="0 0 24 24"
					>
						<path
							fill="currentColor"
							d="M21.329 7.207c0-1.212-.472-2.352-1.329-3.207s-1.996-1.329-3.207-1.329a4.5 4.5 0 0 0-3.18 1.304c-.027.025-7.967 7.964-7.967 7.964c-.373.373-.717.91-.967 1.514c-.195.473-1.979 5.863-2.336 6.939a1 1 0 0 0 1.263 1.263c1.076-.355 6.465-2.141 6.938-2.336c.603-.248 1.14-.592 1.515-.967l2.157-2.156l.076.01c.64 0 1.28-.244 1.769-.732l4.5-4.5a2.487 2.487 0 0 0 .588-2.572c.107-.386.18-.783.18-1.195zM9.465 17.586c-.406.143-1.145.393-2.038.691l-1.704-1.704c.301-.894.551-1.634.691-2.038l3.051 3.051zm-4.097.047l.999.999l-1.498.499l.499-1.498zm7.698-3.113l-2.42 2.42l-.235.18l-3.53-3.529l.18-.234l7.131-7.131l2.731 2.731l-3.69 3.69c-.513.512-.549 1.289-.167 1.873zm6.08-4.959l-4.5 4.5a.502.502 0 0 1-.708 0a.5.5 0 0 1 0-.707l4.5-4.5a.502.502 0 0 1 .707 0a.499.499 0 0 1 .001.707zm.107-1.764a1.489 1.489 0 0 0-1.521.35l-.102.102l-2.731-2.731l.078-.078c.984-.98 2.652-.981 3.608-.023a2.52 2.52 0 0 1 .743 1.793c.001.199-.03.394-.075.587z"
						></path>
					</svg>
					<span>
						{t("multiple.select_the_products_to_be_imported")}
					</span>
				</div>
			}
			primaryAction={{
				content:
					selectedProductsIds.length === 0 ? (
						t("multiple.select_at_least_one_product")
					) : (
						<Trans
							i18nKey="multiple.import_x_products"
							values={{ x: selectedProductsIds.length }}
						></Trans>
					),
				disabled: selectedProductsIds.length === 0 || importDisabled,
				onAction: submitProductsToUpload,
			}}
			secondaryActions={[
				{
					content: t("multiple.export_to_csv"),
					onAction: exportToCSV,
					loading: csvLoading,
					disabled:
						selectedProductsIds.length === 0 || importDisabled,
				},
			]}
			footer={
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: "0.5rem",
					}}
				>
					{state.hasValidKopyPlusSubscription ? (
						<CreditsCostPreview context="multiple" />
					) : (
						""
					)}
					{state.hasValidKopyPlusSubscription &&
					insufficientCredits ? (
						<CoolButton
							onClick={() =>
								setAIState({ isCreditsTopUpOverlayOpen: true })
							}
							innerPadding="0.25rem 0.5rem"
							fontSize="0.85rem"
							outerPadding="0.2rem"
						>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									gap: "0.2rem",
								}}
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="1rem"
									height="1rem"
									viewBox="0 0 24 24"
								>
									<path
										fill="currentColor"
										d="M19 20H5a1 1 0 0 0 0 2h14a1 1 0 0 0 0-2M8.71 7.71L11 5.41V17a1 1 0 0 0 2 0V5.41l2.29 2.3a1 1 0 0 0 1.42 0a1 1 0 0 0 0-1.42l-4-4a1 1 0 0 0-.33-.21a1 1 0 0 0-.76 0a1 1 0 0 0-.33.21l-4 4a1 1 0 1 0 1.42 1.42"
									></path>
								</svg>
								<span>{t("multiple.top-up_credits")}</span>
							</div>
						</CoolButton>
					) : (
						""
					)}
				</div>
			}
		>
			{state.hasValidKopyPlusSubscription ? (
				<Modal.Section>
					<Heading>{t("ai.enhance_your_products_with_ai")}</Heading>
					<div style={{ marginTop: "1rem" }}>
						<AIBulkOrdersButtons />
					</div>
				</Modal.Section>
			) : null}

			<Modal.Section>
				<Heading>
					{t("multiple.tags_to_be_added_to_all_products")}
				</Heading>
				<p style={{ marginTop: "1rem" }}>
					{t("multiple.add_multiple_by_separating_with_a_comma")}
				</p>
				<FormLayout>
					<TextField
						value={tagString}
						onChange={(change) => setTagString(change)}
						connectedRight={
							<Button onClick={addTag}>{t("common.add")}</Button>
						}
					/>
					<Stack spacing="extraTight">
						{tags.map((tag) => (
							<Tag key={tag} onRemove={() => removeTag(tag)}>
								{tag}
							</Tag>
						))}
					</Stack>
				</FormLayout>
			</Modal.Section>

			<Modal.Section>
				<div
					style={{ display: "flex", justifyContent: "space-between" }}
				>
					<div style={{ marginBottom: "0.6rem" }}>
						<Heading>{t("multiple.product_collections")}</Heading>
					</div>
					<div style={{ display: "flex", gap: "0.6rem" }}>
						<Button
							onClick={() => {
								setShowCollectionCreator(true)
								setShowCollectionPicker(false)
							}}
						>
							{t("multiple.create_collection")}
						</Button>
						<Button
							onClick={() => {
								setShowCollectionPicker(true)
								setShowCollectionCreator(false)
							}}
						>
							{t("multiple.select_collections")}
						</Button>
					</div>
				</div>
				<div style={{ marginTop: "1rem" }}>
					{showCollectionCreator ? (
						<CollectionCreator
							discard={() => setShowCollectionCreator(false)}
							onCollectionCreated={(newCollection) => {
								let alreadySelectedCollectionsIDs = []
								setShowCollectionCreator(false)
								setCollectionsCreated((prev) => prev + 1)
								setCollectionsSelected(
									[
										...collectionsSelected,
										newCollection,
									].filter((c) => {
										if (
											!alreadySelectedCollectionsIDs.includes(
												c.id,
											)
										) {
											alreadySelectedCollectionsIDs.push(
												c.id,
											)
											return true
										} else {
											return false
										}
									}),
								)
							}}
						/>
					) : collectionsSelected.length > 0 ? (
						<Banner
							status="info"
							title={`${t("multiple.products_will_be_added_to")} ${collectionsSelected.map((c) => c.title).join(", ")}`}
						></Banner>
					) : (
						<Banner
							status="info"
							title={t(
								"multiple.products_will_be_added_to_the_default_collection",
							)}
						></Banner>
					)}
				</div>

				<CollectionPicker
					open={showCollectionPicker}
					onCancel={() => setShowCollectionPicker(false)}
					onSelection={(selections) => {
						setCollectionsSelected(
							selections.map((selection) => {
								return {
									title: selection.title,
									id: selection.id,
								}
							}),
						)
					}}
				/>
			</Modal.Section>

			<Modal.Section>
				<div style={{ marginBottom: "0.6rem" }}>
					<Heading>{t("multiple.product_status")}</Heading>
				</div>
				<Checkbox
					label={t("multiple.mark_products_as_active")}
					checked={statusActiveChecked}
					onChange={(change) => changeStatusActiveChecked(change)}
				/>
			</Modal.Section>

			<Modal.Section>
				<div style={{ marginBottom: "0.6rem" }}>
					<Heading>{t("multiple.publish_product")}</Heading>
				</div>
				<Checkbox
					label={t(
						"multiple.publish_products_to_online_store_on_import",
					)}
					checked={publishChecked}
					onChange={(change) => changePublishChecked(change)}
				/>
			</Modal.Section>

			<Modal.Section>
				<div style={{ marginBottom: "0.6rem" }}>
					<Heading>{t("multiple.taxable")}</Heading>
				</div>
				<Checkbox
					label={t("multiple.set_products_to_be_imported_as_taxable")}
					checked={taxable}
					onChange={() => setTaxable(!taxable)}
				/>
			</Modal.Section>

			<Modal.Section>
				<div style={{ marginBottom: "0.6rem" }}>
					<Heading>{t("multiple.product_prices_rounding")}</Heading>
				</div>
				<PriceRounding
					roundingEnabled={roundingEnabled}
					onCheckedChanged={(change) => setRoundingEnabled(change)}
					roundingNumber={roundingNumber}
					onRoundingNumberChange={(change) =>
						setRoundingNumber(change)
					}
				/>
			</Modal.Section>

			<Modal.Section>
				<div style={{ marginBottom: "0.6rem" }}>
					<Heading>
						{t("multiple.product_vendor_applied_to_all_products")}
					</Heading>
				</div>
				<VendorField
					vendor={vendor}
					onVendorChange={(change) => setVendor(change)}
				/>
			</Modal.Section>

			<Modal.Section>
				<div style={{ marginBottom: "0.6rem" }}>
					<Heading>{t("multiple.products_to_import")}</Heading>
				</div>
				<Card>
					<ProductsSelector
						products={products}
						selectedProductsIds={selectedProductsIds}
						setSelectedProductsIds={setSelectedProductsIds}
						roundingEnabled={roundingEnabled}
						roundingNumber={roundingNumber}
						storeCurrency={state.shopCurrency}
					/>
				</Card>
			</Modal.Section>

			<ConfirmBeforeClosingModal
				opened={confirmBeforeClosingModalIsOpen}
				setOpened={setConfirmBeforeClosingModalIsOpen}
				onConfirmClose={props.onModalClose}
			/>

			{showUpdateScopesModal && (
				<div
					style={{
						position: "fixed",
						top: "0",
						left: "0",
						right: "0",
						bottom: "0",
						height: "100%",
						width: "100%",
						zIndex: "550",
						background: "rgba(0, 0, 0, 0.3)",
						backdropFilter: "blur(5px)",
						display: "flex",
						flexDirection: "column",
						gap: "1rem",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					<div
						style={{
							background: "#fcfcfc",
							padding: "1.5rem",
							borderRadius: "1rem",
						}}
					>
						<ScopesUpdateTutorial
							context="publishedField"
							requiredScopes={["write_publications"]}
							hasNeccessaryScopes={state.currentScopes?.includes(
								"write_publications",
							)}
							onRequiredScopesChecked={() => {
								setPublishChecked(true)
								setShowUpdateScopesModal(false)
							}}
							videoSrc="https://kopy.b-cdn.net/kopy_publications_scopes_tutorial.mp4"
						/>
						<div style={{ marginTop: "1rem" }}>
							<Button
								onClick={() => setShowUpdateScopesModal(false)}
							>
								Cancel
							</Button>
						</div>
					</div>
				</div>
			)}
		</Modal>
	)
}
